import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { animeList } from "@/mocks/anime";
import { useSeo } from "@/hooks/useSeo";
import AnimeCard from "@/components/feature/AnimeCard";

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get("q") ?? "";
  const [keyword, setKeyword] = useState(query);

  useEffect(() => {
    setKeyword(query);
  }, [query]);

  useSeo({
    title: query
      ? `“${query}”的搜索结果 - 樱花动漫网站`
      : "搜索动漫 - 樱花动漫网站",
    description: query
      ? `樱花动漫网站关于“${query}”的搜索结果，快速查找相关动漫资源与作品信息。`
      : "在樱花动漫网站搜索你想看的动漫，支持按名称、类型、地区等关键词快速查找。",
    keywords: `${query || "动漫搜索"},樱花动漫网站搜索,动漫资源查找,动漫在线观看`,
  });

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return animeList.filter((item) => {
      const haystack = [
        item.title,
        item.region,
        item.studio,
        String(item.year),
        ...item.genres,
        item.description,
      ]
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [query]);

  const hotRecommend = useMemo(
    () => [...animeList].sort((a, b) => b.views - a.views).slice(0, 8),
    [],
  );

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const q = keyword.trim();
    setSearchParams(q ? { q } : {});
  };

  return (
    <div className="mx-auto px-4 py-6 md:px-8 md:py-8">
      <div className="rounded-lg border border-background-200/70 bg-primary-50/50 p-5 md:p-6">
        <h1 className="font-heading text-2xl font-extrabold text-foreground-950 md:text-3xl">
          搜索动漫
        </h1>
        <p className="mt-2 text-sm text-foreground-600">
          输入动漫名称、类型或地区关键词，快速找到你想看的作品。
        </p>

        <form onSubmit={submit} className="mt-4 flex flex-col gap-3 sm:flex-row" role="search">
          <div className="relative flex-1">
            <span className="pointer-events-none absolute left-4 top-1/2 flex h-4 w-4 -translate-y-1/2 items-center justify-center text-foreground-400">
              <i className="ri-search-line text-base" />
            </span>
            <input
              type="search"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="例如：恋爱、冒险、2024、日本…"
              className="w-full rounded-md border border-background-200 bg-background-50 py-3 pl-11 pr-4 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-300 focus:ring-2 focus:ring-primary-200"
            />
          </div>
          <button
            type="submit"
            className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-8 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
          >
            <i className="ri-search-line" />
            搜索
          </button>
        </form>

        {query && (
          <p className="mt-4 text-sm text-foreground-600">
            关键词：<strong className="text-primary-700">{query}</strong> · 共找到{" "}
            <strong className="text-primary-700">{results.length}</strong> 部相关动漫
          </p>
        )}
      </div>

      {!query ? (
        <section className="mt-10">
          <h2 className="font-heading text-xl font-bold text-foreground-950">热门推荐</h2>
          <p className="mt-1 text-sm text-foreground-500">还没想好搜什么？先看看大家都在追的作品。</p>
          <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {hotRecommend.map((anime) => (
              <AnimeCard key={anime.id} anime={anime} showDescription />
            ))}
          </div>
        </section>
      ) : results.length > 0 ? (
        <section className="mt-8">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {results.map((anime) => (
              <AnimeCard key={anime.id} anime={anime} showDescription />
            ))}
          </div>
        </section>
      ) : (
        <section className="mt-8">
          <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-50 py-14 text-center">
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-secondary-100 text-2xl text-secondary-500">
              <i className="ri-search-2-line" />
            </span>
            <h2 className="mt-4 font-heading text-lg font-bold text-foreground-900">
              未找到相关动漫，但推荐以下热门作品
            </h2>
            <p className="mt-2 max-w-md text-sm text-foreground-500">
              换个关键词试试，比如作品名称、类型（热血 / 恋爱）或年份（2024）。
            </p>
          </div>

          <h3 className="mt-8 font-heading text-xl font-bold text-foreground-950">热门作品</h3>
          <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {hotRecommend.map((anime) => (
              <AnimeCard key={anime.id} anime={anime} showDescription />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}